<template>
  <div class="bg-card rounded-lg shadow p-6">
    <!-- Process Selector Header -->
    <div class="mb-6 flex justify-between items-center">
      <h2 class="text-xl font-semibold">Document Management</h2>
      <div class="flex items-center">
        <label for="process-selector" class="mr-2 text-sm font-medium text-gray-700">Forecasting Process:</label>
        <select id="process-selector" v-model="selectedProcessId" @change="handleProcessChange"
          class="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-64 p-2.5"
          :disabled="loading">
          <option v-if="loading" value="">Loading processes...</option>
          <option v-else-if="processes.length === 0" value="">No processes available</option>
          <option v-for="process in processes" :key="process.id" :value="process.id">
            {{ process.name }} ({{ formatProcessDate(process.start_date) }})
          </option>
        </select>
        <div v-if="loading" class="ml-2">
          <svg class="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none"
            viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
        </div>
      </div>
    </div>

    <!-- Kanban Board with Side Panel -->
    <div class="flex gap-4">
      <!-- Kanban Board -->
      <div :class="['grid grid-cols-1 md:grid-cols-4 gap-4 overflow-x-auto', selectedDocument ? 'w-2/3' : 'w-full']">
        <!-- Creating Column -->
        <div
          class="bg-background rounded-md p-4 border border-border min-w-[250px] h-[calc(100vh-200px)] flex flex-col">
          <h3 class="text-sm font-bold mb-4 flex items-center justify-between">
            <div class="flex items-center">
              <div class="w-3 h-3 rounded-full bg-green-700 mr-2"></div>
              {{ formatDOCUMENT_STATUS(DOCUMENT_STATUS.CREATING) }}
              <button
                @click="preparePlannerTemplate"
                class="ml-2 px-2 py-1 text-xs rounded-md transition-colors flex items-center bg-primary text-white hover:bg-primary/90"
              >
                <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                  </path>
                </svg>
                Prepare Templates
              </button>
            </div>
            <span class="text-xs bg-gray-200 text-gray-700 rounded-full px-2 py-0.5">{{ creatingDocuments.length
            }}</span>
          </h3>

          <div class="flex-1 overflow-y-auto space-y-3 pr-2">
            <div v-for="(doc, index) in creatingDocuments" :key="'creating-' + index"
              class="bg-white p-3 rounded shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
              @click="selectDocument(doc)" :class="{ 'ring-2 ring-primary': selectedDocument?.id === doc.id }">
              <div class="flex justify-between items-start">
                <div class="font-medium text-sm">{{ doc.name }}</div>
                <div class="text-xs px-2 py-0.5 rounded-full font-medium" :class="{
                  'bg-green-100 text-green-800': doc.status === DOCUMENT_STATUS.CREATING,
                  'bg-blue-100 text-blue-800': doc.status === DOCUMENT_STATUS.REVIEW_CREATED,
                  'bg-yellow-100 text-yellow-800': doc.status === DOCUMENT_STATUS.EMAIL_PENDING
                }">
                  {{ formatDOCUMENT_STATUS(doc.status) }}
                </div>
              </div>
              <div class="flex justify-between items-center mt-1">
                <div class="text-xs text-gray-500">{{ doc.description }}</div>
                <div v-if="doc.report_type" class="text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white font-medium ml-2">
                  {{ doc.report_type }}
                </div>
              </div>
              <div class="text-xs text-gray-500 mt-1">By: {{ doc.created_by_name }}</div>
            </div>
          </div>
        </div>

        <!-- Combined Ready for Download and Downloaded Column -->
        <div
          class="bg-background rounded-md p-4 border border-border min-w-[250px] h-[calc(100vh-200px)] flex flex-col">
          <!-- Ready for Download Section -->
          <h3 class="text-sm font-bold mb-4 flex items-center justify-between">
            <div class="flex items-center">
              <div class="w-3 h-3 rounded-full bg-yellow-400 mr-2"></div>
              {{ formatDOCUMENT_STATUS(DOCUMENT_STATUS.READY_FOR_DOWNLOAD) }}
            </div>
            <span class="text-xs bg-gray-200 text-gray-700 rounded-full px-2 py-0.5">{{ readyForDownloadDocuments.length
            }}</span>
          </h3>

          <div :class="[
            'overflow-y-auto space-y-3 pr-2 mb-6',
            readyForDownloadDocuments.length > 0 ? 'flex-1' : '',
            downloadedDocuments.length === 0 && readyForDownloadDocuments.length > 0 ? 'flex-grow' : ''
          ]">
            <div v-for="(doc, index) in readyForDownloadDocuments" :key="'ready-' + index"
              class="bg-white p-3 rounded shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
              @click="selectDocument(doc)" :class="{ 'ring-2 ring-primary': selectedDocument?.id === doc.id }">
              <div class="font-medium text-sm">{{ doc.name }}</div>
              <div class="flex justify-between items-center mt-1">
                <div class="text-xs text-gray-500">{{ doc.description }}</div>
                <div v-if="doc.report_type" class="text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white font-medium ml-2">
                  {{ doc.report_type }}
                </div>
              </div>
              <div class="text-xs text-gray-500 mt-1">By: {{ doc.created_by_name }}</div>
            </div>
          </div>

          <!-- Divider -->
          <div class="border-t border-gray-200 my-4"></div>

          <!-- Downloaded Section -->
          <h3 class="text-sm font-bold mb-4 flex items-center justify-between">
            <div class="flex items-center">
              <div class="w-3 h-3 rounded-full bg-green-700 mr-2"></div>
              {{ formatDOCUMENT_STATUS(DOCUMENT_STATUS.DOWNLOADED) }}
            </div>
            <span class="text-xs bg-gray-200 text-gray-700 rounded-full px-2 py-0.5">{{ downloadedDocuments.length
            }}</span>
          </h3>

          <div :class="[
            'overflow-y-auto space-y-3 pr-2',
            downloadedDocuments.length > 0 ? 'flex-1' : '',
            readyForDownloadDocuments.length === 0 && downloadedDocuments.length > 0 ? 'flex-grow' : ''
          ]">
            <div v-for="(doc, index) in downloadedDocuments" :key="'downloaded-' + index"
              class="bg-white p-3 rounded shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
              @click="selectDocument(doc)" :class="{ 'ring-2 ring-primary': selectedDocument?.id === doc.id }">
              <div class="font-medium text-sm">{{ doc.name }}</div>
              <div class="flex justify-between items-center mt-1">
                <div class="text-xs text-gray-500">{{ doc.description }}</div>
                <div v-if="doc.report_type" class="text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white font-medium ml-2">
                  {{ doc.report_type }}
                </div>
              </div>
              <div class="text-xs text-gray-500 mt-1">By: {{ doc.created_by_name }}</div>
            </div>
          </div>
        </div>

        <!-- Combined Uploaded and Checking / Adjustment Needed Column -->
        <div
          class="bg-background rounded-md p-4 border border-border min-w-[250px] h-[calc(100vh-200px)] flex flex-col">
          <!-- Uploaded and Checking Section -->
          <h3 class="text-sm font-bold mb-4 flex items-center justify-between">
            <div class="flex items-center">
              <div class="w-3 h-3 rounded-full bg-green-700 mr-2"></div>
              {{ formatDOCUMENT_STATUS(DOCUMENT_STATUS.UPLOADED_CHECKING) }}
            </div>
            <span class="text-xs bg-gray-200 text-gray-700 rounded-full px-2 py-0.5">{{
              uploadedAndCheckingDocuments.length }}</span>
          </h3>

          <div :class="[
            'overflow-y-auto space-y-3 pr-2 mb-6',
            uploadedAndCheckingDocuments.length > 0 ? 'flex-1' : '',
            adjustmentNeededDocuments.length === 0 && uploadedAndCheckingDocuments.length > 0 ? 'flex-grow' : ''
          ]">
            <div v-for="(doc, index) in uploadedAndCheckingDocuments" :key="'uploaded-' + index"
              class="bg-white p-3 rounded shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
              @click="selectDocument(doc)" :class="{ 'ring-2 ring-primary': selectedDocument?.id === doc.id }">
              <div class="font-medium text-sm">{{ doc.name }}</div>
              <div class="flex justify-between items-center mt-1">
                <div class="text-xs text-gray-500">{{ doc.description }}</div>
                <div v-if="doc.report_type" class="text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white font-medium ml-2">
                  {{ doc.report_type }}
                </div>
              </div>
              <div class="text-xs text-gray-500 mt-1">By: {{ doc.created_by_name }}</div>
            </div>
          </div>

          <!-- Divider -->
          <div class="border-t border-gray-200 my-4"></div>

          <!-- Adjustment Needed Section -->
          <h3 class="text-sm font-bold mb-4 flex items-center justify-between">
            <div class="flex items-center">
              <div class="w-3 h-3 rounded-full bg-yellow-400 mr-2"></div>
              {{ formatDOCUMENT_STATUS(DOCUMENT_STATUS.ADJUSTMENT_NEEDED) }}
            </div>
            <span class="text-xs bg-gray-200 text-gray-700 rounded-full px-2 py-0.5">{{ adjustmentNeededDocuments.length
            }}</span>
          </h3>

          <div :class="[
            'overflow-y-auto space-y-3 pr-2',
            adjustmentNeededDocuments.length > 0 ? 'flex-1' : '',
            uploadedAndCheckingDocuments.length === 0 && adjustmentNeededDocuments.length > 0 ? 'flex-grow' : ''
          ]">
            <div v-for="(doc, index) in adjustmentNeededDocuments" :key="'adjustment-' + index"
              class="bg-white p-3 rounded shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
              @click="selectDocument(doc)" :class="{ 'ring-2 ring-primary': selectedDocument?.id === doc.id }">
              <div class="font-medium text-sm">{{ doc.name }}</div>
              <div class="flex justify-between items-center mt-1">
                <div class="text-xs text-gray-500">{{ doc.description }}</div>
                <div v-if="doc.report_type" class="text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white font-medium ml-2">
                  {{ doc.report_type }}
                </div>
              </div>
              <div class="text-xs text-gray-500 mt-1">By: {{ doc.created_by_name }}</div>
            </div>
          </div>
        </div>

        <!-- Ready for Revision Column (Compact Layout) -->
        <div
          class="bg-background rounded-md p-4 border border-border min-w-[250px] h-[calc(100vh-200px)] flex flex-col">
          <h3 class="text-sm font-bold mb-4 flex items-center justify-between">
            <div class="flex items-center">
              <div class="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
              {{ formatDOCUMENT_STATUS(DOCUMENT_STATUS.REVISION_NEEDED) }}

              <!-- Info Icon for Document Types Status -->
              <button @click="showDocumentTypesInfo" class="ml-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                title="Show document types status">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </div>
            <div class="flex items-center">
              <span class="text-xs bg-gray-200 text-gray-700 rounded-full px-2 py-0.5 mr-2">[{{
                availableDocumentTypesCount }}/{{ allReportTypes.length }}]</span>

              <!-- Merge Documents Button -->
              <button v-if="readyForRevisionDocuments.length > 0" @click="mergeRevisionDocuments" :disabled="isMerging"
                class="text-xs px-2 py-1 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
                <svg v-if="isMerging" class="animate-spin -ml-1 mr-2 h-3 w-3 text-white"
                  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                  </path>
                </svg>
                {{ isMerging ? 'Merging...' : 'Merge Documents' }}
              </button>
            </div>
          </h3>

          <div class="flex-1 overflow-y-auto space-y-2 pr-2">
            <div v-for="(doc, index) in readyForRevisionDocuments" :key="'revision-' + index"
              class="bg-white py-2 px-3 rounded shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
              @click="selectDocument(doc)" :class="{ 'ring-2 ring-primary': selectedDocument?.id === doc.id }">
              <div class="font-medium text-sm">{{ doc.name }}</div>
              <div class="flex justify-between items-center mt-1">
                <div class="text-xs text-gray-500">{{ doc.description }}</div>
                <div v-if="doc.report_type" class="text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white font-medium ml-2">
                  {{ doc.report_type }}
                </div>
              </div>
              <div class="text-xs text-gray-500 mt-1">By: {{ doc.created_by_name }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Document Detail Side Panel -->
      <transition enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="transform translate-x-full opacity-0" enter-to-class="transform translate-x-0 opacity-100"
        leave-active-class="transition-all duration-300 ease-in" leave-from-class="transform translate-x-0 opacity-100"
        leave-to-class="transform translate-x-full opacity-0">
        <div v-if="selectedDocument" class="w-1/3 bg-white rounded-md shadow p-6 flex flex-col h-[calc(100vh-200px)]">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">Document Details</h3>
            <button @click="clearSelectedDocument" class="text-gray-500 hover:text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd" />
              </svg>
            </button>
          </div>

          <!-- Document Details Content -->
          <div class="space-y-6 overflow-y-auto flex-1 pr-2">
            <!-- Document Name with Process Name in brackets -->
            <div>
              <h4 class="text-sm font-medium text-gray-500 mb-1">Document Name</h4>
              <div v-if="isEditingName" class="flex items-center">
                <input
                  v-model="editedName"
                  ref="nameInput"
                  class="text-lg font-semibold border border-gray-300 rounded-md mx-1 p-1 w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  @keyup.enter="saveDocumentName"
                  @blur="saveDocumentName"
                />
              </div>
              <div v-else class="flex items-center group">
                <p class="text-lg font-semibold">
                  {{ selectedDocument.name }}
                  <span v-if="selectedDocument.process_name" class="text-gray-500">
                    ({{ selectedDocument.process_name }})
                  </span>
                </p>
                <button
                  @click="startEditingName"
                  class="ml-1 text-gray-500 hover:text-primary focus:outline-none transition-colors opacity-0 group-hover:opacity-100 relative z-10"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="mb-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Status and Owner in one row -->
            <div class="flex justify-between items-start">
              <!-- Document Status (left) -->
              <div class="w-1/2">
                <h4 class="text-sm font-medium text-gray-500 mb-1">Status</h4>
                <div class="flex items-center">
                  <div class="w-3 h-3 rounded-full mr-2" :class="{
                    'bg-green-700': selectedDocument.status === DOCUMENT_STATUS.CREATING ||
                      selectedDocument.status === DOCUMENT_STATUS.DOWNLOADED ||
                      selectedDocument.status === DOCUMENT_STATUS.UPLOADED_CHECKING,
                    'bg-yellow-400': selectedDocument.status === DOCUMENT_STATUS.READY_FOR_DOWNLOAD ||
                      selectedDocument.status === DOCUMENT_STATUS.ADJUSTMENT_NEEDED,
                    'bg-blue-500': selectedDocument.status === DOCUMENT_STATUS.REVISION_NEEDED
                  }"></div>
                  <span class="text-sm">{{ formatDOCUMENT_STATUS(selectedDocument.status) }}</span>
                </div>
              </div>

              <!-- Document Owner (right) -->
              <div class="w-1/2">
                <h4 class="text-sm font-medium text-gray-500 mb-1">Owner</h4>
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <img v-if="selectedDocument.created_by_avatar_url" :src="selectedDocument.created_by_avatar_url"
                      alt="Avatar" class="h-8 w-8 rounded-full mr-2" />
                    <div v-else class="h-8 w-8 rounded-full bg-gray-200 mr-2 flex items-center justify-center">
                      <span class="text-gray-500 text-sm">{{ selectedDocument.created_by_name?.charAt(0) || '?'
                      }}</span>
                    </div>
                  </div>
                  <div>
                    <p class="text-sm font-medium">{{ selectedDocument.created_by_name }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div>
              <h4 class="text-sm font-medium text-gray-500 mb-1">Description</h4>
              <p class="text-sm">{{ selectedDocument.description }}</p>
            </div>

            <!-- Actions -->
            <div class="pt-4 border-t border-gray-200">
              <h4 class="text-sm font-medium text-gray-500 mb-3">Actions</h4>
              <div class="space-y-2">
                <button :class="[
                  'w-full px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2',
                  selectedDocument.status === DOCUMENT_STATUS.READY_FOR_DOWNLOAD
                    ? 'bg-primary text-white hover:bg-primary/90 focus:ring-primary'
                    : 'bg-transparent text-gray-700 border border-gray-500 hover:bg-gray-100 focus:ring-gray-500'
                ]" @click="selectedDocument?.id && downloadDocument(selectedDocument.id)">
                  Download Document
                </button>

                <!-- Review and Send Email to Planner button - only visible when document status is "ADJUSTMENT_NEEDED" -->
                <button v-if="selectedDocument.status === DOCUMENT_STATUS.ADJUSTMENT_NEEDED"
                  @click="openEmailReviewModal"
                  class="w-full mt-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
                  Review and Send Email to Planner
                </button>

                <!-- Review Excel button - only visible when document status is "REVIEW_CREATED" -->
                <button v-if="selectedDocument.status === DOCUMENT_STATUS.REVIEW_CREATED"
                  @click="openSpreadsheetReviewModal"
                  class="w-full mt-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
                  Review Prefilled Template
                </button>

                <!-- Review Email button - only visible when document status is "EMAIL_PENDING" -->
                <button v-if="selectedDocument.status === DOCUMENT_STATUS.EMAIL_PENDING" @click="openEmailReviewModal"
                  class="w-full mt-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
                  Review and Send Email to Planner
                </button>

                <!-- Review Uploaded Template button - only visible when document status is "UPLOADED_CHECKING" -->
                <button v-if="selectedDocument.status === DOCUMENT_STATUS.UPLOADED_CHECKING"
                  @click="openUploadedTemplateReviewModal"
                  class="w-full mt-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
                  Review Uploaded Template and Findings
                </button>
              </div>
            </div>

            <!-- Comments Section -->
            <div class="pt-4 border-t border-gray-200">
              <h4 class="text-sm font-medium text-gray-500 mb-3">Comments</h4>
              <div v-if="comments.length === 0" class="text-sm text-gray-500 italic">
                No comments yet
              </div>
              <div v-else class="space-y-4">
                <div v-for="comment in comments" :key="comment.id" class="bg-gray-50 rounded-lg p-3">
                  <div class="flex items-start justify-between mb-2">
                    <div class="flex items-center">
                      <div class="flex-shrink-0">
                        <img :src="comment.created_by_avatar_url" :alt="comment.created_by_name"
                          class="h-8 w-8 rounded-full object-cover" />
                      </div>
                      <div class="ml-2">
                        <p class="text-sm font-medium">{{ comment.created_by_name || 'Unknown User' }}</p>
                        <p class="text-xs text-gray-500">
                          {{ new Date(comment.created_at).toLocaleDateString('sl-SI', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric'
                          }).replace(',', '') }},
                          {{ new Date(comment.created_at).toLocaleTimeString('sl-SI', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          }) }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p class="text-sm text-gray-700">{{ comment.content }}</p>
                </div>
              </div>

              <!-- New Comment Input -->
              <div class="mt-4">
                <div class="flex gap-2">
                  <input v-model="newComment" type="text" placeholder="Add a comment..."
                    class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    @keyup.enter="addComment" />
                  <button @click="addComment" :disabled="!newComment.trim()"
                    class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed">
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Uploaded Template Review Modal -->
    <UploadedTemplateReviewModal :is-open="showUploadedTemplateReviewModal" :document-data="selectedDocument || {}"
      @close="closeUploadedTemplateReviewModal" @status-updated="handleStatusUpdate" />

    <!-- Document Types Info Modal -->
    <DocumentTypesInfoModal :is-open="showDocumentTypesInfoModal" :document-types-status="documentTypesStatus"
      :show-merge-anyway-button="readyForRevisionDocuments.length > 0 && !allDocumentTypesAvailable && isModalOpenedFromMergeButton"
      :merged-documents="mergedDocuments" @close="closeDocumentTypesInfoModal" @merge-anyway="performMerge"
      @download-document="downloadMergedDocument" @delete-document="deleteMergedDocument" />
      
    <!-- Planner Template Status Modal -->
    <PlannerTemplateStatusModal
      :is-open="showPlannerTemplateStatusModal"
      :forecast-id="selectedProcessId"
      :planner-status="plannerTemplateStatus"
      @close="showPlannerTemplateStatusModal = false"
      @prepare-template="preparePlannerTemplateForType"
      @prepare-all-missing="prepareAllMissingTemplates"
    />
  </div>

  <!-- Loading Indicator -->
  <div v-if="isLoading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white p-6 rounded-lg shadow-lg">
      <div class="flex items-center">
        <svg class="animate-spin h-5 w-5 mr-3 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none"
          viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
          </path>
        </svg>
        <span>Preparing templates...</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { DOCUMENT_STATUS, REPORT_TYPE } from '@/types/document';
import { useDocuments, useForecastingProcess } from '@/composables/useDatabase';
import UploadedTemplateReviewModal from './UploadedTemplateReviewModal.vue';
import DocumentTypesInfoModal from './DocumentTypesInfoModal.vue';
import PlannerTemplateStatusModal from './PlannerTemplateStatusModal.vue';
import { logger, LogCategory } from '@/services/logger';
import { supabase } from '@/services/supabase';
import { ExcelMergeService } from '@/features/forecasting/services/excelMergeService';
import { jobService } from '@/services/jobService';
import { JOB_TYPE, JOB_STATUS } from '@/types/job';
import { systemConfigService } from '@/features/system-config';

export default defineComponent({
  name: 'ForecastingPCODocuments',
  components: {
    UploadedTemplateReviewModal,
    DocumentTypesInfoModal,
    PlannerTemplateStatusModal
  },
  emits: ['open-email-modal', 'open-spreadsheet-modal'],
  setup(props, { emit }) {

    // Use the forecasting process composable to fetch processes
    const {
      processes,
      loading,
      fetchProcesses
    } = useForecastingProcess();

    // Selected process ID
    const selectedProcessId = ref<string>('');

    // Use the documents composable to fetch data from the database

    const {
      documents,
      selectedDocument,
      comments,
      fetchDocumentsByForecastId,
      insertComment,
      selectDocument,
      clearSelectedDocument,
      downloadDocument,
      updateDocumentStatus
    } = useDocuments();

    // New comment input
    const newComment = ref('');
    
    // Document name editing
    const isEditingName = ref(false);
    const editedName = ref('');
    const nameInput = ref<HTMLInputElement | null>(null);

    // Modal states
    const showEmailReviewModal = ref(false);
    const showSpreadsheetReviewModal = ref(false);
    const showUploadedTemplateReviewModal = ref(false);
    const showDocumentTypesInfoModal = ref(false);
    const showPlannerTemplateStatusModal = ref(false);
    const isModalOpenedFromMergeButton = ref(false);
    const isLoading = ref(false);
    
    // Planner template status
    const plannerTemplateStatus = ref<Record<string, { exists: boolean, loading: boolean }>>({});

    // Merging state
    const isMerging = ref(false);

    // Merged documents list
    const mergedDocuments = ref<any[]>([]);

    // All report types (excluding CAL)
    const allReportTypes = Object.values(REPORT_TYPE).filter(type => type !== REPORT_TYPE.CAL);

    // Document types status
    const documentTypesStatus = computed(() => {
      const status: Record<string, boolean> = {};

      // Initialize all report types as false (missing)
      allReportTypes.forEach(type => {
        status[type] = false;
      });

      // Mark available report types as true
      readyForRevisionDocuments.value.forEach(doc => {
        // Try to get report_type from the document
        let reportType: string | null = null;

        // 1. Try to use the report_type property if it exists
        if (doc.report_type) {
          reportType = String(doc.report_type).toUpperCase();
        }
        // 2. If not, try to extract it from the name
        else if (doc.name) {
          // Try to extract report type from name (e.g., "2025_responsible planners report_AIR_template.xlsx")
          const match = doc.name.match(/_([A-Z]{2,})_/);
          if (match && match[1]) {
            reportType = match[1].toUpperCase();
          }
        }

        // If we found a report type, mark it as available
        if (reportType) {
          // Check if this report type exists in our enum
          if (Object.values(REPORT_TYPE).includes(reportType as REPORT_TYPE)) {
            status[reportType] = true;
          } else {
            // Try to match by name (e.g., if report_type is "Air" instead of "AIR")
            for (const enumType of Object.values(REPORT_TYPE)) {
              if (reportType === enumType.toUpperCase()) {
                status[enumType] = true;
                break;
              }
            }
          }
        }
      });

      return status;
    });

    // Check if all required document types are available
    const allDocumentTypesAvailable = computed(() => {
      // Check if all report types have a corresponding document
      return allReportTypes.every(type => {
        return documentTypesStatus.value[type] === true;
      });
    });

    // Count of available document types
    const availableDocumentTypesCount = computed(() => {
      return Object.values(documentTypesStatus.value).filter(status => status).length;
    });

    // Format process date (shorter format)
    const formatProcessDate = (dateInput: string | Date): string => {
      if (!dateInput) return '';

      const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short'
      });
    };

    // Handle process selection change
    const handleProcessChange = async () => {
      logger.info('Forecasting process selected for documents view', LogCategory.GENERAL, {
        processId: selectedProcessId.value
      });

      // Save the selected process ID to localStorage
      if (selectedProcessId.value) {
        localStorage.setItem('selectedForecastingProcessId', selectedProcessId.value);
      }

      // Set a timeout to reset loading state if it gets stuck
      const loadingTimeout = setTimeout(() => {
        if (loading.value === true) {
          logger.warn('Loading state stuck in handleProcessChange, resetting', LogCategory.GENERAL);
          loading.value = false;
        }
      }, 10000); // 10 second timeout

      try {
        if (selectedProcessId.value) {
          await fetchDocumentsByForecastId(selectedProcessId.value);
        }
      } catch (error) {
        logger.error('Error fetching documents for process', LogCategory.GENERAL, {
          processId: selectedProcessId.value,
          error
        });
        // Reset loading state in case of error
        loading.value = false;
      } finally {
        // Clear the timeout
        clearTimeout(loadingTimeout);
      }
    };

    // Fetch processes and documents when component is mounted
    onMounted(async () => {
      await fetchProcesses();

      // Check if there's a saved process ID in localStorage
      const savedProcessId = localStorage.getItem('selectedForecastingProcessId');
      
      if (savedProcessId && processes.value.some(p => p.id === savedProcessId)) {
        // Use the saved process ID if it exists and is valid
        selectedProcessId.value = savedProcessId;
      } else if (processes.value.length > 0) {
        // Otherwise, select the newest process (they're already sorted by created_at in descending order)
        selectedProcessId.value = processes.value[0].id;
        // Save the selected process ID to localStorage
        localStorage.setItem('selectedForecastingProcessId', selectedProcessId.value);
      }
      
      // Fetch documents for the selected process
      if (selectedProcessId.value) {
        await fetchDocumentsByForecastId(selectedProcessId.value);
      }

      // Lade die zusammengeführten Dokumente aus dem Supabase Bucket
      try {
        const mergeService = new ExcelMergeService();
        const documents = await mergeService.getMergedDocuments();
        mergedDocuments.value = documents;

        logger.info('Loaded merged documents from Templates bucket', LogCategory.GENERAL, {
          count: documents.length,
          documentIds: documents.map(doc => doc.id)
        });
      } catch (error) {
        logger.error('Error loading merged documents', LogCategory.GENERAL, { error });
      }
    });

    // Computed properties to filter documents by status using DOCUMENT_STATUS enum
    // Exclude documents with report_type "CAL" from all kanban lanes
    const creatingDocuments = computed(() =>
      documents.value.filter(doc =>
        (doc.status === DOCUMENT_STATUS.CREATING ||
          doc.status === DOCUMENT_STATUS.REVIEW_CREATED ||
          doc.status === DOCUMENT_STATUS.EMAIL_PENDING) &&
        doc.report_type !== REPORT_TYPE.CAL
      )
    );

    const readyForDownloadDocuments = computed(() =>
      documents.value.filter(doc =>
        doc.status === DOCUMENT_STATUS.READY_FOR_DOWNLOAD &&
        doc.report_type !== REPORT_TYPE.CAL
      )
    );

    const downloadedDocuments = computed(() =>
      documents.value.filter(doc =>
        doc.status === DOCUMENT_STATUS.DOWNLOADED &&
        doc.report_type !== REPORT_TYPE.CAL
      )
    );

    const uploadedAndCheckingDocuments = computed(() =>
      documents.value.filter(doc =>
        doc.status === DOCUMENT_STATUS.UPLOADED_CHECKING &&
        doc.report_type !== REPORT_TYPE.CAL
      )
    );

    const adjustmentNeededDocuments = computed(() =>
      documents.value.filter(doc =>
        doc.status === DOCUMENT_STATUS.ADJUSTMENT_NEEDED &&
        doc.report_type !== REPORT_TYPE.CAL
      )
    );

    const readyForRevisionDocuments = computed(() =>
      documents.value.filter(doc =>
        doc.status === DOCUMENT_STATUS.REVISION_NEEDED &&
        doc.report_type !== REPORT_TYPE.CAL
      )
    );

    // State to track if COM documents exist
    const comDocumentsExist = ref(false);
    const checkingDocuments = ref(false);

    // Function to check if COM documents exist directly in the database
    const checkCOMDocumentsExist = async () => {
      try {
        checkingDocuments.value = true;
        console.log('Checking for COM documents in database...');
                
        // Query the documents table directly
        const { data, error } = await supabase
          .from('documents')
          .select('*')
          .eq('report_type', 'COM')
          .eq('forecast_id', selectedProcessId.value)
          .in('status', [
            DOCUMENT_STATUS.CREATING,
            DOCUMENT_STATUS.REVIEW_CREATED,
            DOCUMENT_STATUS.READY_FOR_DOWNLOAD,
            DOCUMENT_STATUS.DOWNLOADED,
            DOCUMENT_STATUS.REVISION_NEEDED
          ]);
        
        if (error) {
          console.error('Error checking for COM documents:', error);
          return;
        }
        
        console.log('COM documents query result:', data);
        
        // Update state based on query result
        comDocumentsExist.value = data && data.length > 0;
        console.log('COM documents exist:', comDocumentsExist.value);
      } catch (error) {
        console.error('Exception checking for COM documents:', error);
      } finally {
        checkingDocuments.value = false;
      }
    };
    
    // Check planner template status
    const checkPlannerTemplateStatus = async () => {
      try {
        // Get all report types except CAL
        const reportTypes = Object.values(REPORT_TYPE).filter(type => type !== REPORT_TYPE.CAL);
        
        // Initialize status object
        const status: Record<string, { exists: boolean, loading: boolean }> = {};
        reportTypes.forEach(type => {
          status[type] = { exists: false, loading: false };
        });
        
        // Get the current forecast ID
        

        // Query the documents table for each report type
        const { data, error } = await supabase
          .from('documents')
          .select('*')
          .eq('forecast_id', selectedProcessId.value)
          .in('status', [DOCUMENT_STATUS.CREATING, DOCUMENT_STATUS.REVIEW_CREATED])
          .in('report_type', reportTypes);
        
        if (error) {
          console.error('Error checking planner template status:', error);
          return status;
        }
        
        // Update status based on query results
        if (data) {
          data.forEach(doc => {
            if (doc.report_type && status[doc.report_type]) {
              status[doc.report_type].exists = true;
            }
          });
        }
        
        return status;
      } catch (error) {
        console.error('Exception checking planner template status:', error);
        return {};
      }
    };

    // Check for COM documents when component is mounted
    onMounted(() => {
      checkCOMDocumentsExist();
    });

    // Check if planner template can be prepared
    const canPreparePlannerTemplate = computed(() => {
      // Currently we only check for COM report type
      // In the future, this will check for all report types
      return !comDocumentsExist.value;
    });

    // Function to format document status for display
    const formatDOCUMENT_STATUS = (status: DOCUMENT_STATUS): string => {
      switch (status) {
        case DOCUMENT_STATUS.CREATING:
          return 'Creating';
        case DOCUMENT_STATUS.REVIEW_CREATED:
          return 'Review Created';
        case DOCUMENT_STATUS.EMAIL_PENDING:
          return 'Email Pending';
        case DOCUMENT_STATUS.READY_FOR_DOWNLOAD:
          return 'Ready for Download';
        case DOCUMENT_STATUS.DOWNLOADED:
          return 'Downloaded';
        case DOCUMENT_STATUS.UPLOADED_CHECKING:
          return 'Uploaded and Checking';
        case DOCUMENT_STATUS.ADJUSTMENT_NEEDED:
          return 'Adjustment Needed';
        case DOCUMENT_STATUS.REVISION_NEEDED:
          return 'Ready for Revision';
        default:
          return status;
      }
    };

    // Email Modal functions
    const openEmailReviewModal = async () => {
      if (selectedDocument.value) {
        emit('open-email-modal', selectedDocument.value);
      } else {
        console.error('No document selected for email review');
      }
    };

    // Spreadsheet Modal functions
    const openSpreadsheetReviewModal = () => {
      if (selectedDocument.value) {
        emit('open-spreadsheet-modal', selectedDocument.value);
      } else {
        console.error('No document selected for spreadsheet review');
      }
    };

    // Uploaded Template Review Modal functions
    const openUploadedTemplateReviewModal = () => {
      if (selectedDocument.value && selectedDocument.value.status === DOCUMENT_STATUS.UPLOADED_CHECKING) {
        showUploadedTemplateReviewModal.value = true;
      } else {
        console.error('No document selected or document is not in UPLOADED_CHECKING status');
      }
    };

    const closeUploadedTemplateReviewModal = () => {
      showUploadedTemplateReviewModal.value = false;
    };

    // Document Types Info Modal functions
    const showDocumentTypesInfo = () => {
      isModalOpenedFromMergeButton.value = false; // Manuelles Öffnen
      showDocumentTypesInfoModal.value = true;
    };

    const closeDocumentTypesInfoModal = () => {
      showDocumentTypesInfoModal.value = false;
      isModalOpenedFromMergeButton.value = false; // Flag zurücksetzen
    };

    // Merge Revision Documents function
    const mergeRevisionDocuments = async () => {
      // Wenn nicht alle Dokumenttypen vorhanden sind, öffne das Modal
      if (!allDocumentTypesAvailable.value) {
        isModalOpenedFromMergeButton.value = true;
        showDocumentTypesInfoModal.value = true;
        return; // Beende die Funktion hier, um den Merge-Prozess nicht zu starten
      }

      // Wenn alle Dokumenttypen vorhanden sind, starte den Merge-Prozess direkt
      await performMerge();
    };

    // Perform Merge function (eigentlicher Merge-Prozess)
    const performMerge = async () => {
      try {
        isMerging.value = true;

        logger.info('Starting to merge revision documents', LogCategory.GENERAL);

        // Erstelle eine Instanz des ExcelMergeService
        const mergeService = new ExcelMergeService();

        // Führe den Merge-Prozess durch
        const mergedBuffer = await mergeService.mergeRevisionDocuments();

        // Erstelle ein Dokument für den Download
        const userId = (await supabase.auth.getUser()).data.user?.id || 'unknown';
        const mergedDoc = await mergeService.createMergedDocument(mergedBuffer, userId);

        // Füge das Dokument zur Liste der zusammengeführten Dokumente hinzu
        if (mergedDoc) {
          // Füge das neue Dokument am Anfang der Liste hinzu
          mergedDocuments.value = [mergedDoc, ...mergedDocuments.value];
          logger.info('Added merged document to list', LogCategory.GENERAL, {
            documentId: mergedDoc.id,
            fileName: mergedDoc.name
          });
        }

        logger.info('Successfully merged and created document for storage', LogCategory.GENERAL);

        // Zeige eine Erfolgsmeldung an
        showNotification('Documents successfully merged and stored', 'success');

        // Aktualisiere die Dokumentenliste
        if (selectedProcessId.value) {
          await fetchDocumentsByForecastId(selectedProcessId.value);
        }
      } catch (error) {
        logger.error('Error merging documents', LogCategory.GENERAL, { error });
        showNotification(`Error merging documents: ${(error as any).message}`, 'error');
      } finally {
        isMerging.value = false;
        showDocumentTypesInfoModal.value = false;
      }
    };

    // Download a merged document
    const downloadMergedDocument = async (document: any) => {
      try {
        logger.info('Downloading merged document', LogCategory.GENERAL, {
          documentId: document.id,
          fileName: document.name
        });

        // Erstelle eine Instanz des ExcelMergeService
        const mergeService = new ExcelMergeService();

        // Hole den Dateipfad aus dem Dokument
        const filePath = document.name;

        // Lade die Datei herunter
        const success = await mergeService.downloadMergedDocument(filePath);

        if (success) {
          logger.info('Successfully downloaded merged document', LogCategory.GENERAL, {
            documentId: document.id,
            fileName: document.name
          });

          // Zeige eine Erfolgsmeldung an
          showNotification('Document successfully downloaded', 'success');
        } else {
          logger.error('Failed to download merged document', LogCategory.GENERAL, {
            documentId: document.id,
            fileName: document.name
          });

          // Zeige eine Fehlermeldung an
          showNotification('Failed to download document', 'error');
        }
      } catch (error) {
        logger.error('Error downloading merged document', LogCategory.GENERAL, {
          documentId: document.id,
          fileName: document.name,
          error
        });

        // Zeige eine Fehlermeldung an
        showNotification(`Error downloading document: ${(error as any).message}`, 'error');
      }
    };

    // Delete a merged document
    const deleteMergedDocument = async (document: any) => {
      try {
        // Bestätigung vom Benutzer einholen
        if (!confirm(`Sind Sie sicher, dass Sie "${document.name}" löschen möchten?`)) {
          return;
        }

        logger.info('Deleting merged document', LogCategory.GENERAL, {
          documentId: document.id,
          fileName: document.name
        });

        // Erstelle eine Instanz des ExcelMergeService
        const mergeService = new ExcelMergeService();

        // Hole den Dateipfad aus dem Dokument
        const filePath = document.name;

        // Lösche die Datei
        const success = await mergeService.deleteMergedDocument(filePath);

        if (success) {
          // Entferne das Dokument aus der Liste
          mergedDocuments.value = mergedDocuments.value.filter(doc => doc.id !== document.id);

          logger.info('Successfully deleted merged document', LogCategory.GENERAL, {
            documentId: document.id,
            fileName: document.name
          });

          // Zeige eine Erfolgsmeldung an
          showNotification('Document successfully deleted', 'success');
        } else {
          logger.error('Failed to delete merged document', LogCategory.GENERAL, {
            documentId: document.id,
            fileName: document.name
          });

          // Zeige eine Fehlermeldung an
          showNotification('Failed to delete document', 'error');
        }
      } catch (error) {
        logger.error('Error deleting merged document', LogCategory.GENERAL, {
          documentId: document.id,
          fileName: document.name,
          error
        });

        // Zeige eine Fehlermeldung an
        showNotification(`Error deleting document: ${(error as any).message}`, 'error');
      }
    };

    // Handle status update from the modal
    const handleStatusUpdate = async ({ documentId, newStatus }) => {
      logger.info('Updating document status', LogCategory.GENERAL, {
        documentId,
        newStatus
      });

      try {
        // Update the document status in the database
        const success = await updateDocumentStatus(documentId, newStatus);

        if (success) {
          logger.info('Document status updated successfully', LogCategory.GENERAL, {
            documentId,
            newStatus
          });

          // Refresh the documents list with the current selected process
          if (selectedProcessId.value) {
            await fetchDocumentsByForecastId(selectedProcessId.value);
          }

          // Update the selected document if it's the one we just updated
          if (selectedDocument.value && selectedDocument.value.id === documentId) {
            selectedDocument.value = {
              ...selectedDocument.value,
              status: newStatus
            };
          }
        } else {
          logger.error('Failed to update document status', LogCategory.GENERAL, {
            documentId,
            newStatus
          });
        }
      } catch (error) {
        logger.error('Error updating document status', LogCategory.GENERAL, {
          error,
          documentId,
          newStatus
        });
      }
    };

    // Function to add a new comment
    const addComment = async () => {
      if (!newComment.value.trim() || !selectedDocument.value) return;

      try {
        await insertComment(selectedDocument.value.id, newComment.value.trim());
        newComment.value = ''; // Clear the input after successful submission
      } catch (error) {
        console.error('Failed to add comment:', error);
      }
    };
    
    // Function to start editing document name
    const startEditingName = () => {
      if (!selectedDocument.value) return;
      
      editedName.value = selectedDocument.value.name;
      isEditingName.value = true;
      
      // Focus the input field after it's rendered
      setTimeout(() => {
        if (nameInput.value) {
          nameInput.value.focus();
          nameInput.value.select();
        }
      }, 50);
    };
    
    // Function to save edited document name
    const saveDocumentName = async () => {
      if (!selectedDocument.value || !editedName.value.trim()) {
        isEditingName.value = false;
        return;
      }
      
      try {
        // Only update if the name has changed
        if (editedName.value !== selectedDocument.value.name) {
          logger.info('Updating document name', LogCategory.GENERAL, {
            documentId: selectedDocument.value.id,
            oldName: selectedDocument.value.name,
            newName: editedName.value
          });
          
          // Update the document in the database
          const { error } = await supabase
            .from('documents')
            .update({ name: editedName.value.trim() })
            .eq('id', selectedDocument.value.id);
            
          if (error) {
            throw error;
          }
          
          // Update the local document object
          selectedDocument.value.name = editedName.value.trim();
          
          // Refresh the documents list to reflect the change
          if (selectedProcessId.value) {
            await fetchDocumentsByForecastId(selectedProcessId.value);
          }
          
          showNotification('Document name updated successfully', 'success');
        }
      } catch (error) {
        logger.error('Error updating document name', LogCategory.GENERAL, {
          error,
          documentId: selectedDocument.value.id
        });
        showNotification('Failed to update document name', 'error');
      } finally {
        isEditingName.value = false;
      }
    };

    // Simple notification function (can be replaced with a proper notification system later)
    const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
      console.log(`[${type.toUpperCase()}] ${message}`);
      // In a real implementation, this would show a UI notification
    };

    // Function to call the prepare-planner-template fission function
    // Function to poll for job status updates
    const pollJobStatus = async (jobId: string, intervalMs = 2000, maxAttempts = 30, reportType?: string) => {
      let attempts = 0;
      const interval = setInterval(async () => {
        attempts++;
        
        try {
          const job = await jobService.getJobById(jobId);
          
          if (!job) {
            console.error(`Job with ID ${jobId} not found`);
            clearInterval(interval);
            return;
          }
          
          logger.info('Job status update', LogCategory.GENERAL, {
            jobId,
            status: job.status,
            reportType
          });
          
          // If the job is finished or failed, stop polling
          if (job.status === JOB_STATUS.FINISHED) {
            if (reportType && plannerTemplateStatus.value[reportType]) {
              // Update status for this report type
              plannerTemplateStatus.value[reportType].exists = true;
              plannerTemplateStatus.value[reportType].loading = false;
              
              showNotification(`Template for ${reportType} prepared successfully`, 'success');
            } else {
              showNotification('Planner template prepared successfully', 'success');
              isLoading.value = false;
              await checkCOMDocumentsExist();
            }
            
            clearInterval(interval);
            
            // Refresh the planner template status
            if (reportType) {
              plannerTemplateStatus.value = await checkPlannerTemplateStatus();
            }
            
            // Refresh the documents list to show the newly created template
            if (selectedProcessId.value) {
              await fetchDocumentsByForecastId(selectedProcessId.value);
            }
          } else if (job.status === JOB_STATUS.FAILED) {
            if (reportType && plannerTemplateStatus.value[reportType]) {
              // Reset loading state for this report type
              plannerTemplateStatus.value[reportType].loading = false;
              
              showNotification(`Error preparing template for ${reportType}`, 'error');
            } else {
              showNotification('Error preparing planner template', 'error');
              isLoading.value = false;
            }
            
            clearInterval(interval);
          }
        } catch (error) {
          console.error('Error polling job status:', error);
        }
        
        // Stop polling after max attempts
        if (attempts >= maxAttempts) {
          console.warn(`Stopped polling after ${maxAttempts} attempts`);
          clearInterval(interval);
          
          // Reset loading state if we have a report type
          if (reportType && plannerTemplateStatus.value[reportType]) {
            plannerTemplateStatus.value[reportType].loading = false;
          } else {
            isLoading.value = false;
          }
        }
      }, intervalMs);
      
      // Return the interval ID so it can be cleared if needed
      return interval;
    };

    const preparePlannerTemplate = async () => {
      try {
        // Show loading state
        // isLoading.value = true;
        
        // Check planner template status
        plannerTemplateStatus.value = await checkPlannerTemplateStatus();
        
        // Hide loading state
        isLoading.value = false;
        
        // Show the modal
        showPlannerTemplateStatusModal.value = true;
      } catch (error) {
        console.error('Error preparing planner template:', error);
        showNotification(`Error preparing planner template: ${(error as any).message}`, 'error');
        isLoading.value = false;
      }
    };
    
    // Function to prepare a template for a specific planner
    const preparePlannerTemplateForType = async (reportType: string) => {
      try {
        // Set loading state for this report type
        if (plannerTemplateStatus.value[reportType]) {
          plannerTemplateStatus.value[reportType].loading = true;
        }
        
        // Get the current active forecast ID or use a default
        const forecastId = selectedProcessId.value;

        // Get template ID from system configuration
        const templateId = await getTemplateIdFromConfig();
        
        // Create a job entry
        const job = await jobService.createJob(
          JOB_TYPE.PREPARE_PLANNER_TEMPLATE,
          forecastId
        );
        
        if (!job || !job.id) {
          throw new Error('Failed to create job');
        }
        
        logger.info('Created job for prepare planner template', LogCategory.GENERAL, {
          jobId: job.id,
          forecastId,
          reportType
        });
        
        // Start polling for job status updates
        pollJobStatus(job.id, 2000, 30, reportType);
        
        // Construct the URL for the Fission Function
        const fissionUrl = (process.env.VUE_APP_FISSION_URL || 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/') + 'prepare-planner-template';
        
        // Make the POST request with the job ID and report type
        const response = await fetch(fissionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            forecastId,
            reportType,
            templateId,
            jobId: job.id
          })
        });
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        
        // After successfully calling the planner template API, call the AI-generated template API
        await prepareAIGeneratedTemplateForType(reportType);
        
        // Refresh the documents list to show the newly created template
        if (selectedProcessId.value) {
          await fetchDocumentsByForecastId(selectedProcessId.value);
        }
        
        return result;
      } catch (error) {
        console.error('Error preparing planner template:', error);
        showNotification(`Error preparing planner template: ${(error as any).message}`, 'error');
        
        // Reset loading state
        if (plannerTemplateStatus.value[reportType]) {
          plannerTemplateStatus.value[reportType].loading = false;
        }
      }
    };
    
    // Function to prepare an AI-generated template for a specific planner
    const prepareAIGeneratedTemplateForType = async (reportType: string) => {
      try {
        // Set loading state for this report type (optional - could use a separate state variable)
        if (plannerTemplateStatus.value[reportType]) {
          plannerTemplateStatus.value[reportType].loading = true;
        }
        
        // Get the current active forecast ID or use a default
        const forecastId = selectedProcessId.value;

        // Create a job entry
        const job = await jobService.createJob(
          JOB_TYPE.PREPARE_AI_TEMPLATE,
          forecastId
        );
        
        if (!job || !job.id) {
          throw new Error('Failed to create job for AI template');
        }
        
        logger.info('Created job for prepare AI-generated template', LogCategory.GENERAL, {
          jobId: job.id,
          forecastId,
          reportType
        });
        
        // Start polling for job status updates
        pollJobStatus(job.id, 2000, 30, reportType);
        
        // Construct the URL for the Fission Function
        const fissionUrl = (process.env.VUE_APP_FISSION_URL || 'https://ai-core.dev.azcld.advisori.de/fission/fraport/api/v1/') + 'prepare-aigenerated-template';
        
        // Make the POST request with the job ID and report type
        const response = await fetch(fissionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            forecastId,
            reportType,
            jobId: job.id
          })
        });
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        
        // Refresh the documents list to show the newly created template
        if (selectedProcessId.value) {
          await fetchDocumentsByForecastId(selectedProcessId.value);
        }
        
        return result;
      } catch (error) {
        console.error('Error preparing AI-generated template:', error);
        showNotification(`Error preparing AI-generated template: ${(error as any).message}`, 'error');
        
        // Reset loading state
        if (plannerTemplateStatus.value[reportType]) {
          plannerTemplateStatus.value[reportType].loading = false;
        }
      }
    };
    
    // Function to prepare all missing templates
    const prepareAllMissingTemplates = async () => {
      try {
        // Get all missing report types
        const missingReportTypes = Object.entries(plannerTemplateStatus.value)
          .filter(([, status]) => !status.exists && !status.loading)
          .map(([type]) => type);
        
        // Prepare templates for all missing report types
        for (const reportType of missingReportTypes) {
          await preparePlannerTemplateForType(reportType);
        }
        
        // Refresh the documents list after all templates are created
        if (selectedProcessId.value) {
          await fetchDocumentsByForecastId(selectedProcessId.value);
        }
        
        // Reset the isAllLoading state in the modal
        // This will be picked up by the modal component
        const modalElement = document.querySelector('.planner-template-status-modal');
        if (modalElement) {
          const event = new CustomEvent('all-templates-created');
          modalElement.dispatchEvent(event);
        }
      } catch (error) {
        console.error('Error preparing all missing templates:', error);
        showNotification(`Error preparing all missing templates: ${(error as any).message}`, 'error');
      }
    };

    // Function to get the template ID from system configuration
    const getTemplateIdFromConfig = async (): Promise<string> => {
      try {
        // Get all system configuration
        const configData = await systemConfigService.getAllConfig();
        
        // Find the reference_empty_template configuration
        const templateConfig = configData.find(item => item.property === 'reference_empty_template');
        
        if (templateConfig && templateConfig.value) {
          logger.info('Found template ID in system configuration', LogCategory.GENERAL, {
            templateId: templateConfig.value
          });
          return templateConfig.value;
        }
        
        // If not found in system configuration, try direct Supabase query
        const { data, error } = await supabase
          .from('system_configuration')
          .select('value')
          .eq('property', 'reference_empty_template')
          .single();
        
        if (error) {
          logger.error('Error fetching template ID from Supabase', LogCategory.GENERAL, { error });
        } else if (data && data.value) {
          logger.info('Found template ID in Supabase', LogCategory.GENERAL, {
            templateId: data.value
          });
          return data.value;
        }
        
        // Fallback to default template ID if not found
        const defaultTemplateId = '1d5baf72-a755-4019-b27c-af3981befb35';
        logger.warn('Template ID not found in configuration, using default', LogCategory.GENERAL, {
          defaultTemplateId
        });
        return defaultTemplateId;
      } catch (error) {
        logger.error('Error getting template ID from config', LogCategory.GENERAL, { error });
        // Fallback to default template ID
        return '1d5baf72-a755-4019-b27c-af3981befb35';
      }
    };

    return {
      // Process data
      processes,
      loading,
      selectedProcessId,
      handleProcessChange,
      formatProcessDate,

      // Document data
      documents,
      selectedDocument,
      comments,
      newComment,
      isEditingName,
      editedName,
      nameInput,
      startEditingName,
      saveDocumentName,

      // Document filtering
      creatingDocuments,
      readyForDownloadDocuments,
      downloadedDocuments,
      uploadedAndCheckingDocuments,
      adjustmentNeededDocuments,
      readyForRevisionDocuments,

      // Document actions
      selectDocument,
      clearSelectedDocument,
      downloadDocument,
      addComment,

      // Modal states and functions
      showEmailReviewModal,
      showSpreadsheetReviewModal,
      showUploadedTemplateReviewModal,
      openEmailReviewModal,
      openSpreadsheetReviewModal,
      openUploadedTemplateReviewModal,
      closeUploadedTemplateReviewModal,
      handleStatusUpdate,

      // Formatting
      formatDOCUMENT_STATUS,
      DOCUMENT_STATUS,

      // Prepare Planner Template
      preparePlannerTemplate,
      isLoading,
      canPreparePlannerTemplate,
      checkingDocuments,

      // Document Types Info Modal
      showDocumentTypesInfoModal,
      documentTypesStatus,
      allDocumentTypesAvailable,
      isModalOpenedFromMergeButton,
      showDocumentTypesInfo,
      closeDocumentTypesInfoModal,

      // Merge documents
      isMerging,
      mergeRevisionDocuments,
      performMerge,
      mergedDocuments,
      downloadMergedDocument,
      deleteMergedDocument,

      // Document types
      availableDocumentTypesCount,
      allReportTypes,
      
      // Planner Template Status Modal
      showPlannerTemplateStatusModal,
      plannerTemplateStatus,
      preparePlannerTemplateForType,
      prepareAllMissingTemplates,
      checkPlannerTemplateStatus
    };
  }
});
</script>
